import { Editor } from '@tiptap/core';
import { ActionData } from 'copilot-design-system';
import React from 'react';

import { createActionHandlers } from '../editor-menu/utils';
import { hasSuggestionState } from '../utils';

interface UseEditorActionsReturn {
  getActionState: (actionId: string) => {
    isActive: boolean;
    isDisabled: boolean;
  };
  onAction: (actionData: ActionData) => void;
}

const actionActiveStateMapCheck = {
  bold: (editor: Editor) => editor.isActive('bold'),
  italic: (editor: Editor) => editor.isActive('italic'),
  underline: (editor: Editor) => editor.isActive('underline'),
  strike: (editor: Editor) => editor.isActive('strike'),
  bulletList: (editor: Editor) => editor.isActive('bulletList'),
  orderedList: (editor: Editor) => editor.isActive('orderedList'),
  link: (editor: Editor) => editor.isActive('link'),
  heading1: (editor: Editor) => editor.isActive('heading', { level: 1 }),
  heading2: (editor: Editor) => editor.isActive('heading', { level: 2 }),
  heading3: (editor: Editor) => editor.isActive('heading', { level: 3 }),
  text: (editor: Editor) => editor.isActive('paragraph'),
};

export const useEditorActions = (
  editor: Editor | null,
): UseEditorActionsReturn => {
  const getActionState = React.useCallback(
    (actionId: string) => {
      if (!editor) return { isActive: false, isDisabled: false };

      return {
        isActive:
          actionActiveStateMapCheck[
            actionId as keyof typeof actionActiveStateMapCheck
          ]?.(editor) ?? false,
        isDisabled: false,
      };
    },
    [editor],
  );

  const onAction = React.useCallback(
    (actionData: ActionData) => {
      if (!editor) return;

      const { range } = hasSuggestionState(editor.state)
        ? editor.state.suggestion$
        : { range: { from: 0, to: 0 } };

      const actionHandlers = {
        bold: createActionHandlers(editor, (chain) =>
          chain.focus().toggleMark('bold').run(),
        ),
        italic: createActionHandlers(editor, (chain) =>
          chain.focus().toggleMark('italic').run(),
        ),
        underline: createActionHandlers(editor, (chain) =>
          chain.focus().toggleUnderline().run(),
        ),
        text: createActionHandlers(editor, (chain) =>
          chain.focus().deleteRange(range).setNode('paragraph').run(),
        ),
        heading1: createActionHandlers(editor, (chain) =>
          chain
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 1 })
            .run(),
        ),
        heading2: createActionHandlers(editor, (chain) =>
          chain
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 2 })
            .run(),
        ),
        heading3: createActionHandlers(editor, (chain) =>
          chain
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 3 })
            .run(),
        ),
        bulletList: createActionHandlers(editor, (chain) =>
          chain.focus().deleteRange(range).toggleBulletList().run(),
        ),
        orderedList: createActionHandlers(editor, (chain) =>
          chain.focus().deleteRange(range).toggleOrderedList().run(),
        ),
      };

      const handler =
        actionHandlers[actionData.action as keyof typeof actionHandlers];

      if (handler) {
        handler.action();
      } else {
        editor.chain().focus().deleteRange(range).run();
      }
    },
    [editor],
  );

  return { getActionState, onAction };
};
